import { type SearchResultItem, SearchResultItemType } from '@/@types/search'
import { Link } from '@tanstack/react-router'
import { compact } from 'lodash-es'
import Mark from 'mark.js'
import { AnimatePresence, motion } from 'motion/react'
import { type HTMLAttributes, useCallback, useEffect, useRef } from 'react'
import Avatar from '../Avatar'
import PageLoading from '../PageLoading'
import ScreenName from '../ScreenName'
import PostSearchResultItem from './SearchResultItem/Post'
import styles from './SearchResult.module.css'

export interface SearchResultProps extends HTMLAttributes<HTMLDivElement> {
	data: SearchResultItem[]
	highlightWords: string[]
	isLoading?: boolean
}

export default function SearchResult({
	data,
	highlightWords,
	isLoading,
	...props
}: SearchResultProps) {
	const ref = useRef<HTMLDivElement | null>(null)
	const renderRow = useCallback((row: SearchResultItem, index: number) => {
		switch (row.type) {
			case SearchResultItemType.SECTION_HEADER:
				return (
					<div key={row.title} className={styles.sectionHeader}>
						{row.title}
					</div>
				)

			case SearchResultItemType.SECTION_FOOTER:
				return <div key={compact([row.url, index]).join('-')} className={styles.sectionFooter} />

			case SearchResultItemType.TOPIC:
				return (
					row.id && (
						<Link
							data-clickable-feedback="false"
							to="/topic/$id"
							params={{
								id: row.id,
							}}
							key={row.id}
							className={styles.topicLink}
						>
							<img
								width={40}
								height={40}
								src={row.squarePicture?.smallPicUrl}
								className={styles.topicImage}
							/>
							<div className={styles.topicContent}>
								<div className={styles.topicTitle}>{row.content}</div>
								<div className={styles.topicSubtitle}>{row.subscribersDescription}</div>
							</div>
						</Link>
					)
				)

			case SearchResultItemType.USER_SECTION:
				return (
					<div key="USER_SECTION">
						{row.items?.map((item) => (
							<Link
								to="/u/$username"
								params={{
									username: item.username,
								}}
								key={item.id}
								data-clickable-feedback="true"
								className={styles.userLink}
							>
								<Avatar as="div" data={item} />
								<div className={styles.userContent}>
									<ScreenName data={item} variant="text" />
									<div className={styles.userSubtitle}>{item.briefIntro}</div>
								</div>
							</Link>
						))}
					</div>
				)

			case SearchResultItemType.ORIGINAL_POST:
				return (
					<PostSearchResultItem key={row.id} id={row.id as string} className={styles.postItem} />
				)
		}
	}, [])

	useEffect(() => {
		const container = ref.current
		if (!container) {
			return
		}
		const instance = new Mark(container)
		instance.mark(highlightWords)

		return () => {
			instance.unmark()
		}
	}, [highlightWords])

	return (
		<AnimatePresence>
			{isLoading ? (
				<PageLoading />
			) : (
				<motion.div
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					exit={{
						opacity: 0,
					}}
					ref={ref}
					className={styles.motionDiv}
				>
					{data.map(renderRow)}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
